import { Box, Button, Stack } from '@mui/material';
import { common } from '@mui/material/colors';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import BaseDialog from 'common/components/base-dialog';
import FormProvider from 'common/components/form-provider';
import RequiredLabel from 'common/components/required-label';
import { colors } from 'common/constants';
import React from 'react';
import { useForm } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { exportDataExcelAnswerRaw } from 'services/campaign.service';
import { RootState } from 'plugins/store';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import { toast } from 'react-toastify';

interface IProps {
    open: boolean;
    onClose: (open: boolean) => void;
}
const ExportData: React.FC<IProps> = ({ open, onClose }) => {
    const methods = useForm({
        resolver: yupResolver(
            yup.object().shape({
                all: yup.string().required('This field is required'),
                fromDate: yup.date().nullable(),
                toDate: yup.date().nullable(),
                exportData: yup.array().of(yup.string()),
            })
        ),
        defaultValues: {
            all: '',
            fromDate: new Date(),
            toDate: new Date(),
            exportData: [],
        },
    });
    const currentCampaign = useAppSelector(
        (state: RootState) => state.campaignState.campaign
    );
    const dispatch = useAppDispatch();
    const { register, setValue } = methods;
    const onSubmit = methods.handleSubmit(async (data) => {
        if (!currentCampaign || !currentCampaign.id) return;
        try {
            dispatch(setIsLoading(true));
            const postParams = {
                all: data.all,
                from: data.fromDate,
                to: data.toDate,
                profile_options: (data.exportData || []).join(','),
            };
            const response = await exportDataExcelAnswerRaw(
                currentCampaign.id,
                postParams
            );
            if (response.success) {
                const link = document.createElement('a');
                link.href = response.data.url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                dispatch(setIsLoading(false));
                onClose(false);
                toast.success(response.message);
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    });
    const handleClick = (all: string) => {
        setValue('all', all);
        onSubmit();
    };
    return (
        <>
            <BaseDialog
                open={open}
                onClose={() => onClose(false)}
                title="Tùy chọn xuất dữ liệu"
                fullWidth={false}
                maxWidth={false}
                sxContent={{
                    width: '650px',
                    padding: '40px',
                }}
            >
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <input
                        {...register('all', {
                            value: '',
                        })}
                        type="hidden"
                    />
                    <Box
                        display="flex"
                        flexDirection={'row'}
                        alignItems={'baseline'}
                        justifyContent={'space-between'}
                        mb={2}
                    >
                        <Stack>
                            <RequiredLabel
                                required={false}
                                label="Thời gian trả lời từ ngày"
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: '600',
                                }}
                            />
                            <BaseDatePicker
                                name="fromDate"
                                sx={{
                                    width: '100%',
                                }}
                            />
                        </Stack>
                        <Stack>
                            <RequiredLabel
                                required={false}
                                label="Đến ngày"
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: '600',
                                }}
                            />
                            <BaseDatePicker
                                name="toDate"
                                sx={{
                                    width: '100%',
                                }}
                            />
                        </Stack>
                    </Box>
                    <RequiredLabel
                        required={false}
                        label=" Dữ liệu bao gồm (Hồ sơ)"
                        sx={{
                            fontSize: '20px',
                            fontWeight: '600',
                        }}
                    />
                    <BaseMultiCheckbox
                        name="exportData"
                        sx={{
                            '& .MuiSvgIcon-root': {
                                fontSize: 24,
                            },
                            '& .MuiFormControlLabel-label': {
                                fontWeight: '500',
                                color: common.black,
                            },
                            '&.Mui-checked + .MuiFormControlLabel-label': {
                                color: colors.main,
                                fontWeight: '500',
                            },
                        }}
                        options={[
                            {
                                label: 'Họ và tên',
                                value: 'name',
                            },
                            {
                                label: 'Giới tính',
                                value: 'gender',
                            },
                            {
                                label: 'Ngày sinh',
                                value: 'dob',
                            },
                            {
                                label: 'Nơi ở hiện tại',
                                value: 'address',
                            },
                            {
                                label: 'Tình trạng hôn nhân',
                                value: 'marriage',
                            },
                            {
                                label: 'Con cái',
                                value: 'child',
                            },
                            {
                                label: 'Nghề nghiệp',
                                value: 'career',
                            },
                            {
                                label: 'Lĩnh vực',
                                value: 'career_field',
                            },
                            {
                                label: 'Thu nhập cá nhân/tháng',
                                value: 'monthly_personal_income',
                            },
                            {
                                label: 'Thu nhập gia đình/tháng',
                                value: 'monthly_family_income',
                            },
                            {
                                label: 'Số điện thoại',
                                value: 'phone_number',
                            },
                            {
                                label: 'Email',
                                value: 'email',
                            },
                        ]}
                    />
                    <Stack
                        mt={2}
                        spacing={2}
                        direction="row"
                        justifyContent="center"
                    >
                        <Button
                            sx={{
                                ...styles.btn,
                                color: colors.main,
                            }}
                            variant="outlined"
                            onClick={() => onClose(false)}
                        >
                            Đóng
                        </Button>
                        <Button
                            onClick={() => handleClick('false')}
                            sx={{
                                ...styles.btn,
                                color: (theme) => theme.palette.common.white,
                            }}
                            variant="contained"
                        >
                            Tải theo ngày
                        </Button>
                        <Button
                            onClick={() => handleClick('true')}
                            sx={{
                                ...styles.btn,
                                color: (theme) => theme.palette.common.white,
                            }}
                            variant="contained"
                        >
                            Tải hết
                        </Button>
                    </Stack>
                </FormProvider>
            </BaseDialog>
        </>
    );
};
const styles = createStyleSheet({
    btn: {
        textTransform: 'none',
        borderRadius: '40px',
        minWidth: '116px',
        minHeight: '60px',
        fontSize: '20px',
        fontWeight: '600',
    },
});

export default ExportData;
